import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { AddressAutocomplete } from '@/components/address-autocomplete';
import { SelectField, SwitchField, TextField } from '@/components/form-fields';
import {
  AddButton,
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import {
  addHours,
  createLocation,
  DAY_NAMES,
  deleteHours,
  deleteLicense,
  deleteLocation,
  deleteRegister,
  HOURS_KINDS,
  type HoursKind,
  LICENSE_TYPES,
  type LicenseRecord,
  type LocationRecord,
  listHours,
  listLicenses,
  listLocations,
  listRegisters,
  locationUsage,
  saveLicense,
  saveRegister,
  updateLocation,
} from '@/lib/settings/locations';
import {
  COUNTRY_OPTIONS,
  TIMEZONE_OPTIONS,
  US_STATE_OPTIONS,
  withStoredValue,
} from '@/lib/store-options';
import { useForm } from '@/lib/use-form';

/**
 * Locations: the stores, hubs and warehouses everything else hangs off.
 *
 * Inventory, orders, registers and delivery zones all point at a location, so
 * this screen is the one that has to be right first. Deleting one is guarded
 * by a count of what it still carries -- the foreign keys would refuse it
 * anyway, with a constraint name nobody can act on.
 */

const TYPE_OPTIONS = [
  { value: 'store', label: 'Store' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'delivery_hub', label: 'Delivery hub' },
];

const schema = z.object({
  name: z.string().trim().min(1, 'Name the location').max(120),
  slug: z
    .string()
    .trim()
    .min(1, 'A short code is required')
    .max(60)
    .regex(/^[a-z0-9-]+$/, 'Lower case letters, numbers and dashes'),
  type: z.enum(['store', 'warehouse', 'delivery_hub']),
  addressLine1: z.string().trim().max(200),
  addressLine2: z.string().trim().max(200),
  city: z.string().trim().max(120),
  state: z.string().trim().max(60),
  zip: z.string().trim().max(20),
  country: z.string().trim().min(2).max(2),
  phone: z.string().trim().max(40),
  email: z.string().trim().max(200),
  timezone: z.string().trim().min(1),
  latitude: z.string().trim(),
  longitude: z.string().trim(),
  active: z.boolean(),
  allowsPickup: z.boolean(),
  allowsDelivery: z.boolean(),
  allowsCurbside: z.boolean(),
  allowsDriveThrough: z.boolean(),
  allowsWalkIn: z.boolean(),
  allowsKiosk: z.boolean(),
});

type Values = z.infer<typeof schema>;

function toValues(record: LocationRecord | null): Values {
  return {
    name: record?.name ?? '',
    slug: record?.slug ?? '',
    type: (record?.type as Values['type']) ?? 'store',
    addressLine1: record?.addressLine1 ?? '',
    addressLine2: record?.addressLine2 ?? '',
    city: record?.city ?? '',
    state: record?.state ?? '',
    zip: record?.zip ?? '',
    country: record?.country ?? 'US',
    phone: record?.phone ?? '',
    email: record?.email ?? '',
    timezone: record?.timezone ?? 'America/Los_Angeles',
    latitude: record?.latitude?.toString() ?? '',
    longitude: record?.longitude?.toString() ?? '',
    active: record?.active ?? true,
    allowsPickup: record?.allowsPickup ?? true,
    allowsDelivery: record?.allowsDelivery ?? true,
    allowsCurbside: record?.allowsCurbside ?? false,
    allowsDriveThrough: record?.allowsDriveThrough ?? false,
    allowsWalkIn: record?.allowsWalkIn ?? true,
    allowsKiosk: record?.allowsKiosk ?? false,
  };
}

const number = (value: string) => (value.trim() === '' ? null : Number(value));

export function LocationSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('locations.manage');
  const canCompliance = can('compliance.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<LocationRecord | 'new' | null>(null);

  const locations = useQuery({
    queryKey: ['locations', accountId],
    queryFn: () => listLocations(accountId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['locations', accountId] });
    // The order form, the zone editor and the register picker all list stores.
    queryClient.invalidateQueries({ queryKey: ['stores', accountId] });
  };

  if (locations.isLoading) return <SettingsSkeleton />;

  // Vehicles have their own screen; showing them here would offer two places
  // to rename the same row.
  const rows = (locations.data ?? []).filter((location) => location.type !== 'vehicle');

  if (editing) {
    return (
      <div className="flex max-w-3xl flex-col gap-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => setEditing(null)}
        >
          <ArrowLeft className="size-4" />
          All locations
        </Button>

        <LocationForm
          accountId={accountId}
          record={editing === 'new' ? null : editing}
          canManage={canManage}
          canCompliance={canCompliance}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
          onDeleted={() => {
            refresh();
            setEditing(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="locations.manage" />}

      <SectionCard
        title="Locations"
        description="Every store, hub and warehouse. Stock, orders and registers all belong to one."
        action={
          <AddButton onClick={() => setEditing('new')} disabled={!canManage}>
            Add location
          </AddButton>
        }
      >
        {rows.length ? (
          <ListShell>
            {rows.map((location) => (
              <ListRow
                key={location.id}
                title={location.name}
                subtitle={
                  [location.addressLine1, location.city, location.state]
                    .filter(Boolean)
                    .join(', ') || 'No address'
                }
                badges={
                  <>
                    <Badge variant="secondary">{location.type.replace(/_/g, ' ')}</Badge>
                    {!location.active && <Badge variant="outline">Inactive</Badge>}
                  </>
                }
                onClick={() => setEditing(location)}
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>No locations yet. Add the store you sell from.</EmptyState>
        )}
      </SectionCard>
    </div>
  );
}

function LocationForm({
  accountId,
  record,
  canManage,
  canCompliance,
  onSaved,
  onDeleted,
}: {
  accountId: string;
  record: LocationRecord | null;
  canManage: boolean;
  canCompliance: boolean;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { values, errors, set, submit } = useForm(toValues(record), schema);

  const save = useMutation({
    mutationFn: async (parsed: Values) => {
      const input = {
        name: parsed.name,
        slug: parsed.slug,
        type: parsed.type,
        active: parsed.active,
        addressLine1: parsed.addressLine1 || null,
        addressLine2: parsed.addressLine2 || null,
        city: parsed.city || null,
        state: parsed.state || null,
        zip: parsed.zip || null,
        country: parsed.country,
        phone: parsed.phone || null,
        email: parsed.email || null,
        timezone: parsed.timezone,
        latitude: number(parsed.latitude),
        longitude: number(parsed.longitude),
        allowsPickup: parsed.allowsPickup,
        allowsDelivery: parsed.allowsDelivery,
        allowsCurbside: parsed.allowsCurbside,
        allowsDriveThrough: parsed.allowsDriveThrough,
        allowsWalkIn: parsed.allowsWalkIn,
        allowsKiosk: parsed.allowsKiosk,
      };

      return record
        ? updateLocation(accountId, record.id, input)
        : createLocation(accountId, input);
    },
    onSuccess: () => {
      toast.success(record ? 'Location saved.' : 'Location added.');
      onSaved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!record) return;

      const usage = await locationUsage(accountId, record.id);
      const blocking = Object.entries(usage).filter(([, count]) => count > 0);

      if (blocking.length) {
        throw new Error(
          `This location still has ${blocking
            .map(([what, count]) => `${count} ${what}`)
            .join(', ')}. Deactivate it instead.`,
        );
      }

      await deleteLocation(accountId, record.id);
    },
    onSuccess: () => {
      toast.success('Location deleted.');
      onDeleted();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate>
        <SectionCard
          title={record ? record.name : 'New location'}
          description="Where you hold stock and serve customers. The timezone drives every schedule the store shows."
        >
          <fieldset disabled={!canManage} className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="location-name"
                label="Name"
                placeholder="Farmhouse Petaluma"
                value={values.name}
                error={errors.name}
                onChange={(value) => {
                  set('name', value);

                  // A new location's code follows its name until somebody
                  // edits it; an existing slug is a URL and stays put.
                  if (!record && !values.slug) {
                    set(
                      'slug',
                      value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, ''),
                    );
                  }
                }}
              />
              <TextField
                id="location-slug"
                label="Short code"
                description="Used in URLs and on labels."
                value={values.slug}
                error={errors.slug}
                onChange={(value) => set('slug', value)}
              />
              <SelectField
                id="location-type"
                label="Type"
                value={values.type}
                options={TYPE_OPTIONS}
                error={errors.type}
                onChange={(value) => set('type', value as Values['type'])}
              />
              <SelectField
                id="location-timezone"
                label="Timezone"
                value={values.timezone}
                options={withStoredValue(TIMEZONE_OPTIONS, values.timezone)}
                error={errors.timezone}
                onChange={(value) => set('timezone', value)}
              />
            </div>

            <Field>
              <FieldLabel htmlFor="location-address">Street address</FieldLabel>
              <AddressAutocomplete
                id="location-address"
                value={values.addressLine1}
                onChange={(value) => {
                  set('addressLine1', value);
                  set('latitude', '');
                  set('longitude', '');
                }}
                onResolved={(address) => {
                  set('addressLine1', address.line1);
                  set('city', address.city);
                  set('state', address.state);
                  set('zip', address.zip);
                  set('latitude', String(address.latitude));
                  set('longitude', String(address.longitude));
                }}
                disabled={!canManage}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="location-address2"
                label="Suite / unit"
                value={values.addressLine2}
                error={errors.addressLine2}
                onChange={(value) => set('addressLine2', value)}
              />
              <TextField
                id="location-city"
                label="City"
                value={values.city}
                error={errors.city}
                onChange={(value) => set('city', value)}
              />
              <SelectField
                id="location-state"
                label="State"
                value={values.state}
                options={withStoredValue(US_STATE_OPTIONS, values.state)}
                error={errors.state}
                onChange={(value) => set('state', value)}
              />
              <TextField
                id="location-zip"
                label="ZIP"
                value={values.zip}
                error={errors.zip}
                onChange={(value) => set('zip', value)}
              />
              <SelectField
                id="location-country"
                label="Country"
                value={values.country}
                options={withStoredValue(COUNTRY_OPTIONS, values.country)}
                error={errors.country}
                onChange={(value) => set('country', value)}
              />
              <TextField
                id="location-phone"
                label="Phone"
                type="tel"
                value={values.phone}
                error={errors.phone}
                onChange={(value) => set('phone', value)}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <SwitchField
                id="location-active"
                label="Active"
                description="Inactive locations disappear from pickers without losing history."
                checked={values.active}
                onChange={(checked) => set('active', checked)}
              />
              <SwitchField
                id="location-pickup"
                label="Offers pickup"
                checked={values.allowsPickup}
                onChange={(checked) => set('allowsPickup', checked)}
              />
              <SwitchField
                id="location-delivery"
                label="Fulfils delivery"
                checked={values.allowsDelivery}
                onChange={(checked) => set('allowsDelivery', checked)}
              />
              <SwitchField
                id="location-curbside"
                label="Offers curbside"
                checked={values.allowsCurbside}
                onChange={(checked) => set('allowsCurbside', checked)}
              />
              <SwitchField
                id="location-walkin"
                label="Accepts walk-ins"
                checked={values.allowsWalkIn}
                onChange={(checked) => set('allowsWalkIn', checked)}
              />
              <SwitchField
                id="location-kiosk"
                label="Has a kiosk"
                checked={values.allowsKiosk}
                onChange={(checked) => set('allowsKiosk', checked)}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : record ? 'Save location' : 'Add location'}
              </Button>

              {record && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  Delete
                </Button>
              )}
            </div>
          </fieldset>
        </SectionCard>
      </form>

      {record && (
        <>
          <HoursPanel accountId={accountId} location={record} canManage={canManage} />
          <RegistersPanel accountId={accountId} location={record} canManage={canManage} />
          <LicensesPanel accountId={accountId} location={record} canManage={canCompliance} />
        </>
      )}
    </div>
  );
}

function HoursPanel({
  accountId,
  location,
  canManage,
}: {
  accountId: string;
  location: LocationRecord;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    dayOfWeek: '1',
    kind: 'open' as HoursKind,
    opensAt: '09:00',
    closesAt: '21:00',
  });

  const hours = useQuery({
    queryKey: ['location-hours', accountId, location.id],
    queryFn: () => listHours(accountId, location.id),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['location-hours', accountId, location.id] });

  const add = useMutation({
    mutationFn: () =>
      addHours(accountId, {
        locationId: location.id,
        dayOfWeek: Number(draft.dayOfWeek),
        kind: draft.kind,
        opensAt: draft.opensAt,
        closesAt: draft.closesAt,
      }),
    onSuccess: () => {
      toast.success('Hours added.');
      refresh();
    },
    onError: (error: Error) =>
      toast.error(
        // The exclusion constraint is the real rule; say what it means.
        error.message.includes('exclusion') || error.message.includes('overlap')
          ? 'Those hours overlap another window on the same day.'
          : error.message,
      ),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteHours(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  /** Copy Monday's rows across the week -- the shape almost every store wants. */
  const copyWeek = useMutation({
    mutationFn: async () => {
      const source = (hours.data ?? []).filter((row) => row.dayOfWeek === Number(draft.dayOfWeek));

      if (!source.length) throw new Error('Add that day first, then copy it.');

      for (const day of [0, 1, 2, 3, 4, 5, 6]) {
        if (day === Number(draft.dayOfWeek)) continue;

        for (const row of source) {
          // Each insert can collide with an existing window; one clash must
          // not abandon the rest of the week.
          await addHours(accountId, {
            locationId: location.id,
            dayOfWeek: day,
            kind: row.kind,
            opensAt: row.opensAt,
            closesAt: row.closesAt,
          }).catch(() => undefined);
        }
      }
    },
    onSuccess: () => {
      toast.success('Copied across the week.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="Opening hours"
      description="Separate hours for the door, for pickup and for delivery. Overlapping windows on the same day are refused."
    >
      {hours.data?.length ? (
        <ListShell>
          {hours.data.map((row) => (
            <ListRow
              key={row.id}
              title={`${DAY_NAMES[row.dayOfWeek]} · ${row.opensAt}–${row.closesAt}`}
              subtitle={row.kind === 'open' ? 'Store open' : `${row.kind} only`}
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(row.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>
          No hours set, so the storefront shows no opening times and pickup slots have nothing to
          sit inside.
        </EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <SelectField
            id="hours-day"
            label="Day"
            className="w-40"
            value={draft.dayOfWeek}
            options={DAY_NAMES.map((name, index) => ({ value: String(index), label: name }))}
            onChange={(value) => setDraft((current) => ({ ...current, dayOfWeek: value }))}
          />
          <SelectField
            id="hours-kind"
            label="Applies to"
            className="w-36"
            value={draft.kind}
            options={HOURS_KINDS.map((kind) => ({
              value: kind,
              label: kind === 'open' ? 'Store open' : kind === 'pickup' ? 'Pickup' : 'Delivery',
            }))}
            onChange={(value) => setDraft((current) => ({ ...current, kind: value as HoursKind }))}
          />
          <Field className="w-28">
            <FieldLabel htmlFor="hours-open">Opens</FieldLabel>
            <Input
              id="hours-open"
              type="time"
              value={draft.opensAt}
              onChange={(event) =>
                setDraft((current) => ({ ...current, opensAt: event.target.value }))
              }
            />
          </Field>
          <Field className="w-28">
            <FieldLabel htmlFor="hours-close">Closes</FieldLabel>
            <Input
              id="hours-close"
              type="time"
              value={draft.closesAt}
              onChange={(event) =>
                setDraft((current) => ({ ...current, closesAt: event.target.value }))
              }
            />
          </Field>

          <Button type="button" size="sm" onClick={() => add.mutate()} disabled={add.isPending}>
            Add
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => copyWeek.mutate()}
            disabled={copyWeek.isPending}
          >
            Copy this day to the week
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function RegistersPanel({
  accountId,
  location,
  canManage,
}: {
  accountId: string;
  location: LocationRecord;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const registers = useQuery({
    queryKey: ['registers', accountId],
    queryFn: () => listRegisters(accountId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['registers', accountId] });

  const add = useMutation({
    mutationFn: () => saveRegister(accountId, { name, locationId: location.id, active: true }),
    onSuccess: () => {
      setName('');
      toast.success('Register added.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteRegister(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) =>
      toast.error(
        error.message.includes('violates foreign key')
          ? 'This register has sessions recorded against it. Deactivate it instead.'
          : error.message,
      ),
  });

  const rows = (registers.data ?? []).filter((register) => register.locationId === location.id);

  return (
    <SectionCard
      title="Registers"
      description="The tills at this location. A register session opens against one of these."
    >
      {rows.length ? (
        <ListShell>
          {rows.map((register) => (
            <ListRow
              key={register.id}
              title={register.name}
              badges={!register.active ? <Badge variant="outline">Inactive</Badge> : null}
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(register.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>No registers here yet, so nobody can open a till.</EmptyState>
      )}

      {canManage && (
        <div className="flex items-end gap-3">
          <Field className="flex-1">
            <FieldLabel htmlFor="register-name">Name</FieldLabel>
            <Input
              id="register-name"
              value={name}
              placeholder="Front counter"
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Button
            type="button"
            size="sm"
            disabled={!name.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            Add register
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function LicensesPanel({
  accountId,
  location,
  canManage,
}: {
  accountId: string;
  location: LocationRecord;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    licenseNumber: '',
    type: 'retail' as LicenseRecord['type'],
    state: location.state ?? 'CA',
    expiresAt: '',
  });

  const licenses = useQuery({
    queryKey: ['licenses', accountId],
    queryFn: () => listLicenses(accountId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['licenses', accountId] });

  const add = useMutation({
    mutationFn: () =>
      saveLicense(accountId, {
        licenseNumber: draft.licenseNumber.trim(),
        type: draft.type,
        state: draft.state,
        locationId: location.id,
        issuedAt: null,
        expiresAt: draft.expiresAt || null,
        metrcFacilityLicense: null,
        active: true,
      }),
    onSuccess: () => {
      setDraft((current) => ({ ...current, licenseNumber: '', expiresAt: '' }));
      toast.success('Licence added.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteLicense(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  const rows = (licenses.data ?? []).filter((license) => license.locationId === location.id);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <SectionCard
      title="Licences"
      description="What this location is licensed to do. Metrc reporting is configured per licence."
    >
      {!canManage && <ReadOnlyNotice permission="compliance.manage" />}

      {rows.length ? (
        <ListShell>
          {rows.map((license) => (
            <ListRow
              key={license.id}
              title={license.licenseNumber}
              subtitle={`${license.type.replace(/_/g, ' ')} · ${license.state}${
                license.expiresAt ? ` · expires ${license.expiresAt}` : ''
              }`}
              badges={
                license.expiresAt && license.expiresAt < today ? (
                  <Badge variant="destructive">Expired</Badge>
                ) : null
              }
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(license.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>No licence recorded for this location.</EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <Field className="w-48">
            <FieldLabel htmlFor="license-number">Licence number</FieldLabel>
            <Input
              id="license-number"
              value={draft.licenseNumber}
              placeholder="C10-0000123-LIC"
              onChange={(event) =>
                setDraft((current) => ({ ...current, licenseNumber: event.target.value }))
              }
            />
          </Field>
          <SelectField
            id="license-type"
            label="Type"
            className="w-44"
            value={draft.type}
            options={LICENSE_TYPES.map((type) => ({ value: type, label: type }))}
            onChange={(value) =>
              setDraft((current) => ({ ...current, type: value as LicenseRecord['type'] }))
            }
          />
          <SelectField
            id="license-state"
            label="State"
            className="w-32"
            value={draft.state}
            options={withStoredValue(US_STATE_OPTIONS, draft.state)}
            onChange={(value) => setDraft((current) => ({ ...current, state: value }))}
          />
          <Field className="w-40">
            <FieldLabel htmlFor="license-expires">Expires</FieldLabel>
            <Input
              id="license-expires"
              type="date"
              value={draft.expiresAt}
              onChange={(event) =>
                setDraft((current) => ({ ...current, expiresAt: event.target.value }))
              }
            />
          </Field>
          <Button
            type="button"
            size="sm"
            disabled={!draft.licenseNumber.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            Add licence
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
